import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Heading,
  Icon,
  Image,
  Input,
  Stack,
  Text,
  useColorMode,
  useColorModeValue,
} from '@chakra-ui/react';
import { LockIcon } from '@chakra-ui/icons';
import { useAuth0 } from '@auth0/auth0-react';
import usePinLock from '../hooks/usePinLock';
import useEffectiveBrand from '../hooks/useEffectiveBrand';
import { useAppearance } from '../theme/AppearanceProvider';
import { usePinLockState } from '../theme/PinLockProvider';

// wrong-PIN misses before we give up and force a full Auth0 logout (item 106, N=5)
const MAX_MISSES = 5;

// item 409 — a live clock so the locked screen works as a glanceable lock screen
const useClock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(t);
  }, []);
  return now;
};

const PinLockOverlay: React.FC = () => {
  const { unlock } = usePinLockState();
  const { tryVerify, isVerifying } = usePinLock();
  const { logout, user } = useAuth0();
  const { aurora } = useAppearance();
  const { brand } = useEffectiveBrand();
  const { colorMode } = useColorMode();
  const now = useClock();

  // item 409 — the customer's mark above the clock, silhouetted to the color
  // mode exactly like the sidebar logo stage
  const logoFilter = colorMode === 'light' ? 'brightness(0)' : 'brightness(0) invert(1)';
  const timeText = now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const dateText = now.toLocaleDateString([], {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  const [pin, setPin] = useState('');
  const [misses, setMisses] = useState(0);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // a solid (not translucent) backdrop so the app behind is fully hidden while away
  const backdrop = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', aurora ? 'whiteAlpha.100' : 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'whiteAlpha.300');

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async () => {
    if (pin.length < 4 || isVerifying) return;
    try {
      await tryVerify(pin);
      setPin('');
      setMisses(0);
      setError('');
      unlock();
    } catch {
      const next = misses + 1;
      setPin('');
      if (next >= MAX_MISSES) {
        logout({ returnTo: window.location.origin });
        return;
      }
      setMisses(next);
      const left = MAX_MISSES - next;
      setError(`Incorrect PIN. ${left} attempt${left === 1 ? '' : 's'} left.`);
      inputRef.current?.focus();
    }
  };

  return (
    <Box
      position="fixed"
      inset={0}
      zIndex={2000}
      bg={backdrop}
      sx={aurora ? { backdropFilter: 'var(--bx-glass-veil)' } : undefined}
    >
      <Center h="100%" px={4}>
        <Stack spacing={8} align="center" w="100%">
          <Stack spacing={3} align="center" textAlign="center">
            {brand?.logoDataUrl ? (
              <Image
                src={brand.logoDataUrl}
                maxH="44px"
                maxW="200px"
                objectFit="contain"
                filter={logoFilter}
                alt={brand.companyName}
              />
            ) : (
              <Text fontWeight="bold" fontSize="lg" letterSpacing="0.04em">
                {brand?.companyName ?? 'Basix Books'}
              </Text>
            )}
            <Text fontSize="6xl" fontWeight="light" lineHeight="1" letterSpacing="-0.02em">
              {timeText}
            </Text>
            <Text fontSize="md" color="gray.500">
              {dateText}
            </Text>
          </Stack>
          <Box
            w="100%"
            maxW="380px"
            bg={cardBg}
            borderWidth="1px"
            borderColor={cardBorder}
            borderRadius={aurora ? 'xl' : 'md'}
            boxShadow={aurora ? '0 8px 32px rgba(0, 0, 0, 0.45)' : 'none'}
            sx={aurora ? { backdropFilter: 'var(--bx-glass-panel)' } : undefined}
            p={8}
          >
            <Stack spacing={5} align="center" textAlign="center">
              <Icon as={LockIcon} boxSize="28px" color="brandGreen.500" />
              <Stack spacing={1}>
                <Heading size="md">Locked</Heading>
                <Text fontSize="sm" color="gray.500">
                  Enter your PIN to unlock
                  {user?.email ? ` — ${user.email}` : ''}
                </Text>
              </Stack>

              <Input
                ref={inputRef}
                type="password"
                inputMode="numeric"
                autoComplete="off"
                textAlign="center"
                letterSpacing="0.5em"
                fontSize="xl"
                maxLength={8}
                placeholder="••••"
                value={pin}
                isInvalid={Boolean(error)}
                onChange={(e) => {
                  setError('');
                  setPin(e.target.value.replace(/\D/g, ''));
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              />

              {error && (
              <Text fontSize="sm" color="red.400">{error}</Text>
              )}

              <Button
                w="100%"
                onClick={submit}
                isLoading={isVerifying}
                isDisabled={pin.length < 4}
              >
                UNLOCK
              </Button>

              <Button
                variant="link"
                size="xs"
                fontWeight="normal"
                color="gray.500"
                onClick={() => logout({ returnTo: window.location.origin })}
              >
                Sign out instead
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Center>
    </Box>
  );
};

// Mounted once in App.tsx, OUTSIDE the routed content, so the whole shell freezes
// behind it. Renders nothing until the idle timer locks the session.
const PinLockScreen: React.FC = () => {
  const { locked } = usePinLockState();
  if (!locked) return null;
  return <PinLockOverlay />;
};

export default PinLockScreen;
